import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVolumeHigh, faVolumeXmark, faXmark } from "@fortawesome/free-solid-svg-icons";
import { RingDevice, startRingLiveView, LiveViewState } from "../services/ringService";

// Each view wakes a battery device, so it closes itself after a while.
const MAX_VIEW_MS = 2 * 60 * 1000;

const STATE_TEXT: Record<LiveViewState, string> = {
  connecting: "Connecting…",
  waking: "Waking camera…",
  live: "Live",
  ended: "Ended",
};

/** Full-screen live video from a Ring doorbell or camera. */
export default function RingLiveView({
  device,
  onClose,
}: {
  device: Pick<RingDevice, "id" | "name">;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<LiveViewState>("connecting");
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    let stop: (() => void) | null = null;
    let cancelled = false;
    startRingLiveView(device.id, videoRef.current!, setState)
      .then((s) => {
        if (cancelled) s();
        else stop = s;
      })
      .catch((e) => !cancelled && setError(e?.message || "Couldn't start live view"));
    const timeout = window.setTimeout(onClose, MAX_VIEW_MS);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      cancelled = true;
      stop?.();
      window.clearTimeout(timeout);
      window.removeEventListener("keydown", onKey);
    };
  }, [device.id, onClose]);

  return createPortal(
    <div className="ring-live" role="dialog" aria-label={`Live view: ${device.name}`}>
      <video
        ref={videoRef}
        className="ring-live__video"
        autoPlay
        playsInline
        muted={muted}
      />
      <header className="ring-live__bar">
        <span className={`ring-live__status ${state === "live" ? "is-live" : ""}`}>
          <span className="ring-live__dot" aria-hidden />
          {device.name} · {error ?? STATE_TEXT[state]}
        </span>
        <span className="ring-live__actions">
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? "Turn sound on" : "Turn sound off"}
          >
            <FontAwesomeIcon icon={muted ? faVolumeXmark : faVolumeHigh} />
          </button>
          <button type="button" onClick={onClose} aria-label="Close live view">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </span>
      </header>
      {state === "ended" && !error && (
        <button type="button" className="ring-live__ended" onClick={onClose}>
          The camera ended the view. Tap to close.
        </button>
      )}
    </div>,
    document.body,
  );
}
